import { memo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDetectAdBlock } from 'helpers/useDetectAdBlock';
import { useShowMembershipPopup } from 'helpers/useShowMembershipPopup';
import AdBlockModal from 'ui/design-base/AdBlockModal';
import AdKaoraBanner, { AdSlot } from 'ui/design-base/AdKaoraBanner';
import ErrorBox from 'ui/design-base/ErrorBox';
import MembershipPopupModal from 'ui/design-base/MembershipPopupModal';
import Page from 'ui/design-base/Page';
import PlayerDetails from 'ui/design-base/PlayerDetails';
import PlayerHeader from 'ui/design-base/PlayerHeader';
import { clearPlayerProfile, fetchPlayerProfile } from 'store/playerProfile/playerProfileSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchPlayerUserProfile } from 'store/userProfile/userProfileSlice';

export const PlayerProfilePage = () => {
  const { id } = useParams<string>();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.userProfile.user);
  const { player, loading, error } = useAppSelector((state) => state.playerProfile);
  const adBlockDetected = useDetectAdBlock();
  const showPopup = useShowMembershipPopup();

  useEffect(() => {
    dispatch(clearPlayerProfile());
    dispatch(fetchPlayerProfile(id));
    dispatch(fetchPlayerUserProfile(id));
    return () => {
      dispatch(clearPlayerProfile());
    };
  }, [id, dispatch]);

  const getPageDescription = () =>
    `${
      `${player?.name} - classifica: ${player?.rank} - categoria di età: ${player?.category || ''}` +
      ` - circolo: ${player?.club?.name || ''} - ${player?.club?.region || ''} (${
        player?.club?.province || ''
      }) - Scopri la CLASSIFICA SIMULATA!   ultimi match: `
    }${player?.last_matches?.map(
      (m) => `${(m.has_won ? ' vittoria contro ' : ' sconfitta contro ') + m.first_opponent?.name} ${m.result}`,
    )} - scopri gli altri match più tantissime altre informazioni! `;

  const getPageKeyword = () =>
    `${player?.name} | ${player?.rank} | ${player?.category || ''} | ${player?.gender || ''} | ${
      player?.club?.name || ''
    } | ${player?.club?.region || ''} | ${player?.club?.province || ''}`;

  const jsonLd = player
    ? {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: player?.name,
        description: `"Giocatore di Tennis - ${player?.name} - classifica: ${player?.rank} - categoria di età: ${player?.category || ''} - circolo: ${player?.club?.name || ''} - ${player?.club?.region || ''} (${player?.club?.province || ''})"`,
        memberOf: {
          '@type': 'SportsOrganization',
          name: player?.club?.name,
          address: {
            '@type': 'PostalAddress',
            addressRegion: player?.club?.region,
            addressLocality: player?.club?.province,
          },
        },

        gender: player?.gender || 'male',
        image: user?.thumbnail ? user.thumbnail : '',
      }
    : undefined;

  const renderContent = (
    <>
      <PlayerHeader loading={loading} {...player} user={user} />
      <PlayerDetails loading={loading} player={player} user={user} />
      {showPopup && <MembershipPopupModal />}
    </>
  );

  let pageTitle = 'Profilo del giocatore';
  let pageKeywords;
  let pageDescription = '';
  let content = renderContent;
  if (adBlockDetected) {
    content = (
      <div className='flex w-full justify-center'>
        <AdBlockModal />
      </div>
    );
  } else {
    if (error) {
      content = (
        <div className='flex justify-center'>
          <ErrorBox
            errorType='Giocatore non trovato'
            errorTitle='Qualcosa è andato storto nel caricamento di questo giocatore'
            errorMessage='Prova più tardi. Se avrai nuovamente problemi, scrivici.'
          />
        </div>
      );
    } else if (!loading && player) {
      pageTitle = `${player.name} | Risultati Tennis FITP - Classifica: ${player.rank} - Categoria ${player.category} - ${player.club?.name} - ${player.club?.region} (${player.club?.province}) - Match recenti e tornei`;
      pageKeywords = getPageKeyword();
      pageDescription = getPageDescription();
    }
  }

  return (
    <Page
      title={pageTitle}
      description={pageDescription || pageTitle}
      keywords={pageKeywords}
      showAds={true}
      jsonLd={jsonLd ? JSON.stringify(jsonLd) : undefined}
    >
      <AdKaoraBanner slot={AdSlot.ARTICLE_TOP} />
      <div className='my-3'>{content}</div>
      <AdKaoraBanner slot={AdSlot.ARTICLE_BOTTOM} />
    </Page>
  );
};

export default memo(PlayerProfilePage);
